import { useAuth } from '@/db/useAuth';
import logger from '@/util/logger';
import { storage } from '@rock/db-admin';
import { getDownloadURL, ref, uploadBytesResumable, type UploadMetadata } from 'firebase/storage';
import { useCallback, useState } from 'react';

const log = logger('useStorageUpload');

type UploadError = string | undefined;

function mergeMetadata(userId: string, file: File, base?: UploadMetadata, override?: UploadMetadata): UploadMetadata {
    const merged: UploadMetadata = {
        contentType: file.type,
        ...base,
        ...override,
    };

    merged.customMetadata = {
        uploader: userId,
        ...base?.customMetadata,
        ...override?.customMetadata,
    };

    return merged;
}

export function useStorageUpload(defaultMetadata?: UploadMetadata) {
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<UploadError>(undefined);
    const { user } = useAuth();

    const uploadFile = useCallback(
        async (file: File, storagePath: string, metadata?: UploadMetadata) => {
            if (!user) {
                throw new Error('A signed-in user is required to upload files.');
            }

            setError(undefined);
            setProgress(0);

            try {
                const storageRef = ref(storage, storagePath);
                const uploadMetadata = mergeMetadata(user.uid, file, defaultMetadata, metadata);
                const uploadTask = uploadBytesResumable(storageRef, file, uploadMetadata);

                return await new Promise<string>((resolve, reject) => {
                    uploadTask.on(
                        'state_changed',
                        (snapshot) => {
                            const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setProgress(pct);
                        },
                        (uploadError) => {
                            setError(uploadError.message);
                            reject(uploadError);
                        },
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve(downloadURL);
                        },
                    );
                });
            } catch (err) {
                const errMsg = err instanceof Error ? err.message : String(err);
                setError(errMsg);
                log.error(`Error uploading file: ${errMsg}`, { err });
                throw err;
            }
        },
        [defaultMetadata, user],
    );

    return { uploadFile, progress, error } as const;
}
